import { FormAssociation, StopConfiguration } from '@/models/activity'
import Form from '@/models/form'
import JobFormSubmission from '@/models/job_form_submission'
import { computed, Ref } from 'vue'
import { useStore } from 'vuex-composition-helpers'

type FilledOutForm = { isFilledOut: true } & Partial<FormAssociation> & JobFormSubmission
type UnfilledForm = { isFilledOut: false } & FormAssociation

export type FormWithSubmissionInfo = FilledOutForm | UnfilledForm

export const computeFormsWithSubmissionInfo = (
  formSubmissions: JobFormSubmission[],
  formAssociations: FormAssociation[],
): FormWithSubmissionInfo[] => {
  const submittedForms = formSubmissions.map((submission): FilledOutForm => {
    const matchingAssociation = formAssociations.find((fa) => fa.formUuid === submission.formId)

    return matchingAssociation
      ? { ...matchingAssociation, ...submission, isFilledOut: true }
      : { ...submission, isFilledOut: true }
  })

  const missingRequiredForms: UnfilledForm[] = formAssociations
    .filter((fa) => fa.stopConfiguration === StopConfiguration.AfterStops)
    .filter((fa) => !formSubmissions.some((fs) => fs.formId === fa.formUuid))
    .map((fs) => ({ ...fs, isFilledOut: false }))

  return [...submittedForms, ...missingRequiredForms]
}

export const useGetAdhocFormSubmissions = () => {
  const store = useStore()
  const getFormDefinitionById = computed<(formId: string) => Form>(
    () => store.getters['forms/getById'],
  )

  const getAdhocFormSubmissionsWithFormNames = (
    formSubmissions: JobFormSubmission[],
    formAssociations: FormAssociation[],
  ) => {
    return getAdhocFormSubmissions(formSubmissions, formAssociations, getFormDefinitionById)
  }
  return { getAdhocFormSubmissionsWithFormNames }
}

const getAdhocFormSubmissions = (
  formSubmissions: JobFormSubmission[],
  formAssociations: FormAssociation[],
  getFormDefinitionById: Ref<(formId: string) => Form>,
) => {
  return formSubmissions
    .filter((fs) => formAssociations.findIndex((f) => f.formUuid === fs.formId) === -1)
    .map((fs) => {
      return {
        ...fs,
        formSubmissionId: fs.formSubmissionUuid,
        formName: getFormDefinitionById.value(fs.formId)?.name ?? 'Form',
      }
    })
}
